import { useEffect } from 'react';
import { useInfiniteQuery, useQuery, UseInfiniteQueryResult } from 'react-query';
import { EtqAPI } from 'api';
import { TQuery } from 'api/api.types';
import { AuditResponseDocument, CapaDocument, GapAnalysisDocument } from './etq.types';

type DocumentPage<T> = {
  items: T[];
  paginationMetadata?: { totalCount?: number };
};

const useAllDocumentPages = <T>(
  queryKey: string,
  fetchPage: (pageNumber: number) => Promise<DocumentPage<T>>,
  config: TQuery<T[]> = {}
) => {
  const query: UseInfiniteQueryResult<DocumentPage<T>> = useInfiniteQuery(
    [queryKey],
    ({ pageParam = 1 }) => fetchPage(pageParam),
    {
      staleTime: 1000 * 60 * 5,
      getNextPageParam: (lastPage, allPages) => {
        const loaded = allPages.reduce((count, page) => count + page.items.length, 0);
        const totalCount = lastPage.paginationMetadata?.totalCount;

        // with a total the server tells us when to stop; without one, a short page does
        if (typeof totalCount === 'number') {
          return loaded < totalCount ? allPages.length + 1 : undefined;
        }
        if (lastPage.items.length === 0) return undefined;
        return lastPage.items.length < allPages[0].items.length ? undefined : allPages.length + 1;
      },
      ...(config as any)
    }
  );

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return {
    ...query,
    // every page the dropdowns need, flattened; undefined until the first page lands
    data: query.data?.pages.reduce((acc: T[], page) => [...acc, ...page.items], [])
  };
};

export const useCAPADocuments = (config: TQuery<CapaDocument[]> = {}) =>
  useAllDocumentPages<CapaDocument>('etq-capa-documents', EtqAPI.getCAPADocuments, config);

export const useGAPAnalysisDocuments = (config: TQuery<GapAnalysisDocument[]> = {}) =>
  useAllDocumentPages<GapAnalysisDocument>(
    'etq-gap-analysis-documents',
    EtqAPI.getGAPAnalysisDocuments,
    config
  );

export const useAuditResponseDocuments = (config: TQuery<AuditResponseDocument[]> = {}) =>
  useAllDocumentPages<AuditResponseDocument>(
    'etq-audit-response-documents',
    EtqAPI.getAuditResponseDocuments,
    config
  );

export const useCapaDocumentById = ({ args, ...config }: TQuery<CapaDocument> = {}) => {
  return useQuery(['etq-capa-document', args], () => EtqAPI.getCapaDocumentById(args), {
    staleTime: 1000 * 60 * 5,
    enabled: !!args?.id,
    ...config
  });
};

export const useGAPAnalysisDocumentById = ({
  args,
  ...config
}: TQuery<GapAnalysisDocument> = {}) => {
  return useQuery(
    ['etq-gap-analysis-document', args],
    () => EtqAPI.getGAPAnalysisDocumentById(args),
    {
      staleTime: 1000 * 60 * 5,
      enabled: !!args?.id,
      ...config
    }
  );
};

export const useAuditResponseDocumentById = ({
  args,
  ...config
}: TQuery<AuditResponseDocument> = {}) => {
  return useQuery(
    ['etq-audit-response-document', args],
    () => EtqAPI.getAuditResponseDocumentById(args),
    {
      staleTime: 1000 * 60 * 5,
      enabled: !!args?.id,
      ...config
    }
  );
};
